import { motion } from "framer-motion";
import { Clock, TrendingDown, AlertTriangle, Users } from "lucide-react";
import { LANDING } from "@/constants/testIds";

const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, delay } },
});

const pains = [
  {
    icon: AlertTriangle,
    title: "Sin trazabilidad ni datos del funnel",
    description:
      "Las conversaciones de WhatsApp no quedan registradas en ningún sistema. Sin métricas, es difícil entender cuántas consultas llegan, qué se pregunta y cuántas se convierten en inscripciones.",
    stat: "0",
    statLabel: "registro estructurado de interacciones",
  },
  {
    icon: Clock,
    title: "Tiempo de respuesta variable",
    description:
      "La velocidad de respuesta depende de la disponibilidad del equipo. Los leads que no obtienen respuesta rápida suelen continuar con otras opciones del mercado.",
    stat: "72%",
    statLabel: "abandona si no responden en 5 min",
  },
  {
    icon: TrendingDown,
    title: "Consultas fuera del horario sin atención",
    description:
      "Una parte importante de las consultas llega por fuera del horario de atención. Sin automatización, esas oportunidades quedan sin respuesta hasta el día siguiente.",
    stat: "40%",
    statLabel: "de consultas llegan fuera del horario",
  },
  {
    icon: Users,
    title: "Equipo dedicado a consultas repetitivas",
    description:
      "Gran parte de las consultas son sobre preguntas frecuentes: planes, horarios, sedes y precios. Automatizarlas libera al equipo para enfocarse en conversaciones de mayor valor.",
    stat: "60%",
    statLabel: "del tiempo en preguntas frecuentes",
  },
];

export default function Problem() {
  return (
    <section
      id="problema"
      data-testid={LANDING.problemSection}
      className="bg-[#1F2937] py-20 md:py-28"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          variants={fadeUp(0)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="mb-14"
        >
          <span className="inline-block text-[#38BDF8] text-sm font-semibold tracking-widest uppercase mb-3">
            El problema
          </span>
          <h2 className="font-['Outfit'] text-3xl md:text-4xl font-700 text-white tracking-tight max-w-xl">
            Desafíos frecuentes en la atención por WhatsApp en el sector fitness
          </h2>
        </motion.div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {pains.map((pain, i) => (
            <motion.div
              key={pain.title}
              variants={fadeUp(i * 0.1)}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="bg-white/5 border border-white/10 rounded-2xl p-7 hover:bg-white/8 transition-colors card-hover"
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#38BDF8]/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                  <pain.icon size={20} className="text-[#38BDF8]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-['Outfit'] text-lg font-600 text-white mb-2">
                    {pain.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {pain.description}
                  </p>
                </div>
              </div>
              <div className="mt-5 pt-5 border-t border-white/10 flex items-baseline gap-2">
                <span className="font-['Outfit'] text-2xl font-700 text-[#38BDF8]">
                  {pain.stat}
                </span>
                <span className="text-gray-500 text-sm">{pain.statLabel}</span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bridge */}
        <motion.div
          variants={fadeUp(0.5)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="mt-14 bg-gradient-to-r from-[#38BDF8]/10 to-transparent border border-[#38BDF8]/20 rounded-2xl p-8 flex flex-col md:flex-row items-center gap-6"
        >
          <div className="flex-1">
            <h3 className="font-['Outfit'] text-xl font-700 text-white mb-2">
              Esta propuesta aborda todos estos puntos de forma integrada.
            </h3>
            <p className="text-gray-400 text-sm leading-relaxed">
              Sistema para Gyms centraliza, automatiza y potencia la atención
              al socio en WhatsApp, sin reemplazar al equipo humano, sino
              multiplicando su capacidad y dando trazabilidad completa a cada interacción.
            </p>
          </div>
          <a
            href="#como-funciona"
            className="flex-shrink-0 inline-flex items-center gap-2 bg-[#38BDF8] hover:bg-[#0EA5E9] text-white font-semibold px-6 py-3 rounded-lg transition-colors text-sm whitespace-nowrap"
          >
            Ver la solución
          </a>
        </motion.div>
      </div>
    </section>
  );
}
